export type Player = {
  id: number;
  version: number;
  name: string;
  nicknames?: string;  // Newline-separated
  skills: Record<string, number>;
  totalScore: number;
  isUnknown?: boolean;
  isArchived?: boolean;
  isShared?: boolean;
  selectionDataId: number;
};

export type Team = {
  id: number;
  version: number;
  name: string;
  color: string;
  players?: Player[];
  selectionDataId: number;
};

export type Sport = 'Football' | 'Rugby';

export type SelectionData = {
  id: number;
  version: number;
  players: Player[];
  teams: Team[];
  skillNames: string[];
  sport: Sport;
  maxPlayers: number;
  skillDistribution: SkillDistribution;
};

export type TeamsResponse = {
  teams: Team[];
};

export type CsvUploadResult = {
  playersAdded: number;
  playersUpdated: number;
  errors: string[];
  players: Player[];
};

export type SkillDistribution = {
  developing: number;  // Bottom percentage (e.g., 15)
  average: number;     // Middle-low percentage (e.g., 45 = 15 + 30)
  good: number;        // Middle-high percentage (e.g., 85 = 15 + 30 + 40)
  // excellent is implicitly 100 - good
};

export type DrillDifficulty = 'Beginner' | 'Intermediate' | 'Advanced';

export type DrillCategory = 'Warm-up' | 'Main' | 'Cool-down' | 'Game';

export type AgeGroup = 'Mini' | 'Youth' | 'Junior' | 'Senior';

export type DrillEquipment = Record<string, number>;

export type Dimension = {
  width: number;   // in metres
  height: number;  // in metres
};

export type DrillImageMetadata = {
  id: number;
  fileName: string;
  contentType: string;
  sortOrder: number;
};

export type DrillVideoMetadata = {
  id: number;
  fileName: string;
  contentType: string;
  blobPath: string;
};

export type TrainingDrill = {
  id: number;
  name: string;
  description: string;
  youtubeUrl?: string;
  mediaType?: 'youtube' | 'images' | 'video';
  difficulty: DrillDifficulty;
  playersRequired: number;
  duration: number;
  skills: string[];
  ageGroups: AgeGroup[];
  equipment: DrillEquipment;
  spaceRequired: Dimension;
  category: DrillCategory;
  sport?: Sport;
  isHidden?: boolean;
};

export const isImageDrill = (drill: TrainingDrill): boolean => drill.mediaType === 'images';
export const isVideoDrill = (drill: TrainingDrill): boolean => drill.mediaType === 'video';

export type YouTubeVideoInfo = {
  videoId: string;
  title: string;
  description: string;
  youtubeUrl: string;
  duration: number;
  inferredDifficulty: DrillDifficulty;
  inferredCategory: DrillCategory;
  inferredSkills: string[];
  inferredPlayersRequired: number;
  inferredEquipment: DrillEquipment;
  inferredAgeGroups: AgeGroup[];
};

export type YouTubeSortOrder = 'views' | 'likes' | 'comments' | 'combined';

export type YouTubeSearchRequest = {
  query: string;
  maxResults?: number;
  sortOrder?: YouTubeSortOrder;
  sport?: Sport;
};

export type YouTubeSearchResult = {
  videoId: string;
  title: string;
  description: string;
  youtubeUrl: string;
  thumbnailUrl: string;
  duration: number;
  viewCount: number;
  likeCount: number;
  commentCount: number;
  combinedScore: number;
  inferredDifficulty: DrillDifficulty;
  inferredCategory: DrillCategory;
  inferredSkills: string[];
  inferredPlayersRequired: number;
  inferredEquipment: DrillEquipment;
  inferredAgeGroups: AgeGroup[];
};

export type YouTubeSearchResponse = {
  results: YouTubeSearchResult[];
  nextPageToken?: string;
  totalResults: number;
};

export type DrillFromYouTube = {
  videoId: string;
  title: string;
  description: string;
  youtubeUrl: string;
  duration: number;
  difficulty: DrillDifficulty;
  category: DrillCategory;
  skills: string[];
  ageGroups: AgeGroup[];
  playersRequired: number;
  equipment: DrillEquipment;
  spaceRequired: Dimension;
};

export type BulkCreateDrillsRequest = {
  drills: DrillFromYouTube[];
};

export type BulkCreateDrillsResponse = {
  successCount: number;
  failureCount: number;
  errors: string[];
  createdDrills: TrainingDrill[];
};

// Session Planning Types (Phase 1)

export type SessionFocus =
  | 'Passing & Movement'
  | 'Shooting & Finishing'
  | 'Defending'
  | 'Dribbling & Ball Control'
  | 'Goalkeeping'
  | 'General/Mixed'
  | 'Tactical'
  | 'Fitness'
  | 'Tackling & Defence'
  | 'Passing & Handling'
  | 'Kicking'
  | 'Set Pieces'
  | 'Rucking & Breakdown'
  | 'Back Play'
  | 'Forward Play';

export type PitchSize = 'full' | 'half' | 'quarter' | 'small';

export type PitchPreset = {
  name: string;
  width: number;
  height: number;
};

export type SessionEnd = 'Cooldown' | 'Match';

export type SessionStructure = {
  sessionEnd: SessionEnd;
  mainDrillBlocks: number;
};

export type SessionSetup = {
  coaches: number;
  players: number;
  pitchDimensions: Dimension;
  totalTime: number;
  sessionFocus: SessionFocus;
  ageGroup?: AgeGroup;
  structure?: SessionStructure;
  sport?: Sport;
  minGroupSize?: number;
};

export type PitchArea = {
  x: number;
  y: number;
  width: number;
  height: number;
  label?: string;
};

export type PlayerGroup = {
  count: number;
  groupName: string;
};

export type StationAssignment = {
  id: number;
  assignedCoaches: number[];
  assignedPlayers: PlayerGroup;
  pitchArea: PitchArea;
};

export type DrillGroup = {
  drill: TrainingDrill;
  stations: StationAssignment[];
};

export type Rotation = {
  atMinute: number;
  description: string;
};

export type TimeBlock = {
  id: number;
  name: string;
  startTime: number;
  duration: number;
  drillGroups: DrillGroup[];
  rotations?: Rotation[];
};

export type EquipmentSummary = Record<string, number>;

export type SessionPlan = {
  setup: SessionSetup;
  timeBlocks: TimeBlock[];
  equipmentSummary: EquipmentSummary;
};

// Saved Training Session Types

export type TrainingSession = {
  id: number;
  version: number;
  name: string;
  sessionFocus: SessionFocus;
  totalTime: number;
  coaches: number;
  players: number;
  ageGroup?: AgeGroup;
  sport?: Sport;
  timeBlocks: TimeBlock[];
  equipmentSummary: EquipmentSummary;
  pitchDimensions: Dimension;
  createdAt: string;
};

export type TrainingSessionResponse = {
  session: TrainingSession;
  isEditable: boolean;
};

export type SaveTrainingSessionRequest = {
  name?: string;
  sessionPlan: SessionPlan;
};

export type UpdateTrainingSessionRequest = {
  id: number;
  version: number;
  name?: string;
  sessionPlan: SessionPlan;
};

// Session Note Types

export type SessionNoteResponse = {
  id: number;
  content: string;
  createdAt: string;
};

export type AddSessionNoteRequest = {
  content: string;
};

// Comment & Reaction Types

export type CommentReplyResponse = {
  id: number;
  content: string;
  username: string;
  createdAt: string;
  likes: number;
  dislikes: number;
  userReaction: boolean | null;
  isOwner: boolean;
};

export type CommentResponse = CommentReplyResponse & {
  replies: CommentReplyResponse[];
};

export type AddCommentRequest = {
  content: string;
};

export type ReactionSummaryResponse = {
  likes: number;
  dislikes: number;
  userReaction: boolean | null;
};

export type ToggleReactionRequest = {
  isLike: boolean;
};

// Drill Favourite Types

export type DrillResponse = {
  drill: TrainingDrill;
  isFavourite: boolean;
  images: DrillImageMetadata[]; // populated by backend, defaults to []
  video?: DrillVideoMetadata;
};

export type ToggleFavouriteResponse = {
  isFavourite: boolean;
};

// Drill Swap Types

export type FindSimilarDrillsRequest = {
  currentDrillId: number;
  category: DrillCategory;
  timeBlockDuration: number;
  skills: string[];
  ageGroup?: AgeGroup;
  difficulty?: DrillDifficulty;
};

export type SimilarDrillResult = {
  drill: TrainingDrill;
  matchScore: number;
  matchingSkills: string[];
  isFavourite: boolean;
};

export type FindSimilarDrillsResponse = {
  drills: SimilarDrillResult[];
};

// Reference Data Types

export type AgeGroupOption = {
  value: AgeGroup;
  label: string;
};

export type SelectionSummary = {
  id: number;
  name: string;
  sport: Sport;
  canEdit: boolean;
  canShare: boolean;
  shareType: 'Team' | 'Player';
  playerId?: number;
};

export type ShareSelectionRequest = {
  email: string;
  sharedName: string;
  canEdit: boolean;
  canShare: boolean;
  shareType: 'Team' | 'Player';
  playerId?: number;
};

export type ShareSelectionResponse = {
  sharedWithEmail: string;
  sharedName: string;
  canEdit: boolean;
  canShare: boolean;
  shareType: 'Team' | 'Player';
  playerId?: number;
  playerName?: string;
  invitationSent?: boolean;
};

export type InviteInfoResponse = {
  email: string;
  sharedName: string;
  invitedByName: string;
  sport: string;
  shareType: 'Team' | 'Player';
};

export type AcceptInviteRequest = {
  token: string;
  password: string;
  nickname: string;
};

export type AcceptInviteResponse = {
  token: string;
  username: string;
  nickname: string;
  expiresAt: string;
  isAdmin: boolean;
  isSuperUser: boolean;
};

export type CreateSelectionRequest = {
  name: string;
  sport: Sport;
};

export type SportConfig = {
  skills: string[];
  defaultPlayerSkills: string[];
  sessionFocusOptions: string[];
  defaultMaxPlayers: number;
  equipmentOptions: string[];
};

export type ReferenceData = {
  skills: string[];
  difficulties: DrillDifficulty[];
  categories: DrillCategory[];
  ageGroups: AgeGroupOption[];
  sessionFocusOptions: SessionFocus[];
  pitchPresets: Record<PitchSize, PitchPreset>;
  selections: SelectionSummary[];
  sports: Record<Sport, SportConfig>;
};

// Squad Analysis Types

export type SkillNeed = {
  skillName: string;
  currentScore: number;
  priority: 'High' | 'Medium' | 'Low';
};

export type PlayerTrainingNeeds = {
  playerId: number;
  playerName: string;
  weakSkills: SkillNeed[];
  overallLevel: 'Developing' | 'Average' | 'Good' | 'Excellent';
  isShared?: boolean;
};

export type GroupMember = {
  playerId: number;
  playerName: string;
  skillScore: number;
  isShared?: boolean;
};

export type TrainingGroup = {
  skillFocus: string;
  members: GroupMember[];
  description: string;
};

export type MentorPair = {
  skillFocus: string;
  mentor: GroupMember;
  learner: GroupMember;
};

export type SquadAnalysisResult = {
  playerNeeds: PlayerTrainingNeeds[];
  peerGroups: TrainingGroup[];
  mentorPairs: MentorPair[];
};

// Player Wall Types

export type CreatePlayerWallPostRequest = {
  selectionDataId: number;
  playerIds: number[]; // empty = all shared players
  drillId: number;
  comment: string;
};

export type PlayerWallDrillInfo = {
  id: number;
  name: string;
  description: string;
  youtubeUrl?: string;
  difficulty: DrillDifficulty;
  duration: number;
  category: DrillCategory;
  skills: string[];
};

export type PlayerWallPostResponse = {
  id: number;
  comment: string;
  createdAt: string;
  coachName: string;
  drill: PlayerWallDrillInfo;
};

// Reseed Drills Types

export type ReseedDrillsRequest = {
  deleteExistingDrills: boolean;
  maxVideosPerSearch: number;
  sport?: Sport;
};

export type ReseedDrillsResponse = {
  success: boolean;
  drillsDeleted: number;
  drillsCreated: number;
  searchesPerformed: number;
  duplicatesFiltered: number;
  errors: string[];
  durationSeconds: number;
};

// Admin User Types

export type AdminUser = {
  id: number;
  email: string;
  nickname: string;
  isActive: boolean;
  isAdmin: boolean;
  isSuperUser: boolean;
  createdAt: string;
};

export type UpdateAdminStatusRequest = {
  isAdmin: boolean;
};

// Drill Animation Types

export type AnimationActorType = 'player' | 'coach' | 'ball';
export type AnimationTeam = 'red' | 'blue' | 'neutral';

export type AnimationActor = {
  id: string;
  type: AnimationActorType;
  team: AnimationTeam;
  label: string;
};

export type AnimationPosition = {
  x: number;
  y: number;
};

export type AnimationStep = {
  description: string;
  durationSeconds: number;
  positions: Record<string, AnimationPosition>;
  ballCarrierId?: string;
  annotations?: string[];
};

export type DrillAnimation = {
  pitch: { width: number; height: number };
  actors: AnimationActor[];
  steps: AnimationStep[];
};

// Build From PDF Types

export type DrillMatchSource = 'ExistingLibrary' | 'YouTubeSearch' | 'Unmatched';

export type DrillMatchResult = {
  isMatched: boolean;
  source: DrillMatchSource;
  matchedDrillId?: number;
  matchedDrillName?: string;
  matchScore?: number;
  unmatchedReason?: string;
};

export type ExtractedDrillInfo = {
  originalName: string;
  parsedDuration?: number;
  parsedDescription?: string;
  diagramBase64?: string;
  diagramMediaType?: string;
  matchResult: DrillMatchResult;
};

export type AnimationRequest = {
  name: string;
  description?: string;
  sport?: string;
  category?: string;
  playersRequired?: number;
  durationMinutes?: number;
  diagramBase64?: string;
  diagramMediaType?: string;
};

export type BuildFromPdfSummary = {
  totalDrillsExtracted: number;
  matchedFromLibrary: number;
  matchedFromYouTube: number;
  unmatched: number;
  newDrillsCreated: number;
  warnings: string[];
};

export type BuildFromPdfResponse = {
  sessionPlan: SessionPlan;
  extractedDrills: ExtractedDrillInfo[];
  summary: BuildFromPdfSummary;
};
